import {
  Component,
  computed,
  effect,
  Input,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { PAGE_LABEL_MAT_PAGINATOR_INTL_PROVIDER } from '../../../../shared/providers';
import { to12HourTime } from 'src/app/shared/utils/time-format.util';
import {
  DeviceEvent,
  SeverityLevel,
  SeverityLevels,
} from '../../pages/qoe-page/qoe-page.component.types';
import { Device } from './qoe-data-table.component.types';

@Component({
  selector: 'app-qoe-data-table',
  templateUrl: './qoe-data-table.component.html',
  providers: PAGE_LABEL_MAT_PAGINATOR_INTL_PROVIDER,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class QoeDataTableComponent {
  @Input() isNoContent = false;

  @Input() isLoading = false;

  @Input({ required: true })
  set deviceEvents(value: DeviceEvent[]) {
    this.events$.set(
      value?.sort((a, b) => {
        const dateA = new Date(`${a.date} ${a.begin_time}`).getTime();
        const dateB = new Date(`${b.date} ${b.begin_time}`).getTime();
        return dateB - dateA;
      }) ?? [],
    );
  }

  readonly events$ = signal<DeviceEvent[]>([]);

  readonly devices$ = computed<Device[]>(() => {
    const lookup: Record<string, DeviceEvent[]> = {};
    const events = this.events$();

    events.forEach((event) => {
      event.begin_time = to12HourTime(event.begin_time);
      event.end_time = to12HourTime(event.end_time);
      (lookup[event.device_name] ??= []).push(event);
    });

    return Object.entries(lookup).map(([deviceName, deviceEvents]) => ({
      events: deviceEvents,
      deviceName,
    }));
  });

  readonly selectedDevice$ = signal<Device | null>(null);

  readonly expandedMobileDevice$ = signal<Device | null>(null);

  readonly filteredDevices$ = computed(() => {
    const term = this.searchTerm$().toLowerCase();
    if (!term) return this.devices$();

    return this.devices$().filter((d) =>
      d.deviceName.toLowerCase().includes(term),
    );
  });

  readonly filteredSelectedDeviceEvents$ = computed(() => {
    const deviceEvents = this.selectedDevice$()?.events ?? [];
    const allowedSeverities = Object.entries(this.severityCheckboxes)
      .filter(([_, enabled]) => enabled())
      .map(([level]) => level as SeverityLevel);

    this.pageNumber = 0;
    return deviceEvents.filter((evt) =>
      allowedSeverities.includes(evt.action_label),
    );
  });

  pageNumber = 0;

  readonly SeverityLevel = SeverityLevels;

  readonly searchTerm$ = signal<string>('');

  readonly severityCheckboxes = Object.freeze(
    Object.fromEntries(
      Object.values(SeverityLevels).map((key) => [key, signal<boolean>(true)]),
    ),
  );

  constructor() {
    // If the selected device is no longer in the filtered list, clear it.
    effect(() => {
      const selected = this.selectedDevice$();
      const filtered = this.filteredDevices$();

      if (
        selected &&
        !filtered.some((d) => d.deviceName === selected.deviceName)
      ) {
        this.selectedDevice$.set(null);
      }
    });

    // If nothing is selected, select the first filtered device.
    effect(() => {
      const selected = this.selectedDevice$();
      const filtered = this.filteredDevices$();

      if (!selected && filtered.length > 0) {
        this.selectedDevice$.set(filtered[0]);
      }
    });
  }

  onSelectDevice(device: Device): void {
    this.selectedDevice$.set(device);
    this.expandedMobileDevice$.update((current) =>
      current?.deviceName === device.deviceName ? null : device,
    );
  }
}
